"use client";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { cx } from "lib/cx";
import { useState } from "react";
import { Bars3Icon } from "@heroicons/react/24/outline";
import { ChevronRightIcon, HomeIcon } from "@heroicons/react/24/outline";
import TemplatesIcon from "public/assets/templatesIcon.svg";
import ScoreCheckIcon from "public/icons/checkScoreBlack.svg"
import Image from "next/image";
import { useAppDispatch } from "lib/redux/hooks";
import { setAccessToken } from "lib/redux/uiSlice";

const MobileNavbar = ({ onClose, handleSignOut }: any) => {
  return (
    <div className="relative translate-x-0 transform rounded-2xl bg-white p-2 text-[10px] shadow-2xl transition-transform duration-300">
      <button
        onClick={onClose}
        className="absolute -left-2 -top-1 rounded-full bg-[#4E4E4E] p-[2px]"
      >
        <ChevronRightIcon className="h-4 w-5 text-white" />
      </button>
      <ul className="mx-auto flex w-fit flex-col items-start font-montserrat">
        <li className="w-full">
          <Link href="/" className="flex items-center gap-2">
            <HomeIcon className="h-3 w-3" /> Home
          </Link>
        </li>
        <li className="w-full">
          <Link href="/resume-builder" className="flex items-center gap-2">
            <TemplatesIcon /> Templates
          </Link>
        </li>
        <li className="w-full">
          <Link href="/upload-resume" className="flex items-center gap-2">
            <Image
              className=""
              src="/icons/checkScoreBlack.svg"
              alt="score check icon"
              width={12}
              height={12}
              loading="eager"
            />
            Check Score
          </Link>
        </li>
      </ul>
      <button
        className="btn-gradient mt-1 px-8 py-1 font-poppins"
        onClick={handleSignOut}
      >
        Sign Out
      </button>
    </div>
  );
};

export const Navbar = () => {
  const [activeTab, setActiveTab] = useState("Dashboard");
  const [isMobileNavVisible, setMobileNavVisible] = useState(false);
  const router = useRouter();
  const dispatch = useAppDispatch();

  const handleMobileNavToggle = () => {
    setMobileNavVisible(!isMobileNavVisible);
  };

  const handleCloseMobileNav = () => {
    setMobileNavVisible(false);
  };

  const handleSignOut = () => {
    // Clear any authentication token or session data
    localStorage.removeItem("access-token");
    dispatch(setAccessToken(null));

    // Navigate to the home page
    router.push("/");
  };

  return (
    <header
      aria-label="Site Header"
      className={cx(
        "fade-in-navbar z-50 my-5 flex h-[60px] items-center rounded-2xl bg-white px-6 py-2 text-lg shadow-sm shadow-[#0000000D]",
        "relative"
      )}
    >
      <div className="flex h-10 w-full items-center justify-between">
        <div className="flex items-center gap-24">
          <Link href="/">
            <span className="sr-only">ResumeMate</span>
            <div className="flex items-center gap-1 lg:gap-2">
              <div className="relative h-[28px] w-[24px] md:h-[35px] md:w-[30px] lg:h-[42px] lg:w-[36px] 2xl:h-[51px] 2xl:w-[44px]">
                <Image
                  src="/assets/resumemateLogo.png"
                  alt="ResumeMate"
                  fill={true}
                />
              </div>
              <div className="font-montserrat text-[10px] font-semibold leading-3 md:text-xs md:leading-3 lg:text-xs lg:leading-3 xl:text-sm xl:leading-4 2xl:text-base 2xl:leading-4">
                <span className="text-[#1C75BC]">Resume</span> <br />
                <span className="text-[#27AAE1]">Mate.io</span>
              </div>
            </div>
          </Link>
        </div>
        <button onClick={handleMobileNavToggle} className="block lg:hidden">
          <Bars3Icon className="h-6 w-6" />
        </button>
        <ul className="hidden gap-12 lg:flex">
          <li
            className={`${activeTab === "Home"
                ? "font-semibold text-[#52A1FF]"
                : "font-normal"
              } cursor-pointer`}
          >
            <Link href="/">Home</Link>
          </li>

          <li className="cursor-pointer">
            <Link href="/resume-builder">Templates</Link>
          </li>
          <li className="cursor-pointer">
            <Link href="/upload-resume">Score Check</Link>
          </li>
          <li
            className={`${activeTab === "Dashboard"
                ? "font-semibold text-[#52A1FF]"
                : "font-normal"
              } cursor-pointer`}
          >
            Dashboard
          </li>
        </ul>
        <button
          className="btn-gradient hidden px-8 py-2 font-poppins text-base lg:block"
          onClick={handleSignOut}
        >
          Sign Out
        </button>
      </div>
      <div
        className={cx(
          "absolute right-0 top-5 transition-transform duration-500",
          isMobileNavVisible ? "translate-x-0" : "translate-x-[150%]"
        )}
      >
        <MobileNavbar
          onClose={handleCloseMobileNav}
          handleSignOut={handleSignOut}
        />
      </div>
    </header>
  );
};
